import { SizeDropDownUI } from "./SizeDropDownUI";
import { Size } from "./types";
import { useRouter } from "next/router";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { Variant } from "../../types/types";
import { getSizeReductionLabelInfo } from "../../utils/product/getSizeReductionLabelInfo";
import { getVariantDimensions } from "../../utils/product/getVariantDimensions";
import { isInStoreOnly } from "../../utils/variant/isInStoreOnly";
import { isFutureRelease } from "../../utils/variant/isFutureRelease";
import { createSelectedSizeUrlObj } from "../Sizes/utils/createSelectedSizeUrl";
import { FitToolAndSizeChart } from "../FitToolAndSizeChart/FitToolAndSizeChart";
import { getMainVariant } from "@/utils/product/getMainVariant.ts";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { HTMLAttributes, useEffect, useMemo, useRef, useState } from "react";
import { StandardProps } from "@/components/componentMap.ts";

export const SizeDropDown = (props: StandardProps & { sizes: string }) => {
  const { product, configProps, flags } = props;

  const router = useRouter();

  const { variant, isSelected } = useVariant(product);

  const showPromoOnIndicators = flags.showPromoOnIndicators.value === true;

  const replaceHistoryEntryOnVariantChange =
    flags.replaceHistoryEntryOnVariantChange.value === true;

  const variants: Variant[] = product.variants;

  // This is unpleasant and not very Reacty, but we have no way of receiving a ref from the JLDS Select component
  // in order to disable/enable the first option within it, so we have to manually fetch it from the DOM instead
  const sizeSelect: React.MutableRefObject<HTMLSelectElement | null> =
    useRef(null);
  useEffect(() => {
    sizeSelect.current = document
      .getElementsByTagName("select")
      .namedItem("select-size");
  }, []);

  // Disables/enables the "Select a size" option depending on a valid size being chosen
  const toggleFirstOption = (isSelected: boolean) => {
    const sizeSelectElement = sizeSelect?.current;
    const firstOption = sizeSelectElement?.children[0];

    if (firstOption) {
      if (isSelected) {
        firstOption.setAttribute("disabled", "disabled");
      } else {
        firstOption.removeAttribute("disabled");
        sizeSelectElement.value = "";
      }
    }
  };

  const handleClick = (newSize: string) => {
    const targetVariant =
      variants.find(
        (v: Variant) =>
          v.differentiators?.size === newSize &&
          (variant.differentiators?.colour
            ? v.differentiators.colour == variant.differentiators.colour
            : true),
      ) || getMainVariant(product);
    const targetUrl = createSelectedSizeUrlObj(targetVariant, router.query);
    const changeSizeFunction = replaceHistoryEntryOnVariantChange
      ? router.replace
      : router.push;
    changeSizeFunction(targetUrl, undefined, { shallow: true, scroll: false });
    dispatchMonetateQEvent("product-pages-size-selector-click");
  };

  const sizeVariants = variants.filter((v) => {
    if (variant.differentiators?.colour) {
      return (
        v.differentiators?.size &&
        v.differentiators?.colour === variant.differentiators?.colour
      );
    } else {
      return v.differentiators?.size;
    }
  });

  const uniqueSizes: Size[] = sizeVariants.map((sizeVariant) => {
    const isUnavailable =
      !sizeVariant.availability?.availableToOrder &&
      !isInStoreOnly(sizeVariant) &&
      !isFutureRelease(sizeVariant);

    const { showReduced, label } = getSizeReductionLabelInfo(
      product,
      sizeVariant,
      { showPromoOnIndicators },
      isUnavailable,
    );

    return {
      name: sizeVariant.differentiators?.size || "",
      sublabel: getVariantDimensions(sizeVariant),
      sizeReductionLabel: label,
      showReduced: showReduced,
      unavailable: isUnavailable,
      isSelected: sizeVariant.id === variant.id && isSelected,
      sizeHeadline: sizeVariant.differentiators?.sizeHeadline || "",
      url: {},
    };
  });

  // If there's only one variant, select it by default
  const [singleVariantSelected, setSingleVariantSelected] = useState(false);

  useEffect(() => {
    if (variants.length === 1 && !singleVariantSelected) {
      setSingleVariantSelected(true);
      const variant = variants[0];
      if (variant) {
        const targetUrl = createSelectedSizeUrlObj(variant, router.query);
        router.push(targetUrl, undefined, { shallow: true, scroll: false });
      }
    }
  }, [router, uniqueSizes, variants, singleVariantSelected]);

  // Disable/enable first option if we have a valid size selected
  useEffect(() => {
    toggleFirstOption(isSelected);
  }, [isSelected]);

  const sortedSizes = uniqueSizes.sort((a, b) => {
    return parseInt(a.name || "", 10) - parseInt(b.name || "", 10);
  });

  const isGroupingAllowed = configProps?.allowGrouping === true;

  const FitToolAndSizeChartComponent = useMemo(() => {
    if (!(uniqueSizes.length > 0)) {
      return () => null;
    }
    return function FitToolAndSizeChartComponent(
      htmlProps: HTMLAttributes<HTMLDivElement>,
    ) {
      return (
        <div {...htmlProps}>
          <FitToolAndSizeChart {...props} />
        </div>
      );
    };
  }, [props]);

  return (
    <SizeDropDownUI
      uniqueSizes={sortedSizes}
      handleClick={handleClick}
      isGroupingAllowed={isGroupingAllowed}
      FitToolAndSizeChartComponent={FitToolAndSizeChartComponent}
      withFitToolAndSizeChart={configProps?.withFitToolAndSizeChart === true}
    />
  );
};
